import { useCallback, useEffect, useState } from 'react';

// Persisted collapse state for the desktop sidebar. The mobile drawer
// ignores this and always renders the expanded variant (drawerOpen wins).
//
// Storage key carries the `pb.` prefix used elsewhere in localStorage so
// the portal's preferences cluster cleanly under one namespace.

const STORAGE_KEY = 'pb.sidebar.collapsed';

function readInitial(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // Private mode / disabled storage - default to expanded.
    return false;
  }
}

export function useSidebarCollapsed(): {
  collapsed: boolean;
  setCollapsed: (next: boolean) => void;
  toggleCollapsed: () => void;
} {
  const [collapsed, setCollapsedState] = useState<boolean>(readInitial);

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
    } catch {
      // Best effort; the UI still updates in-memory.
    }
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsedState((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  // Cross-tab sync so collapsing in one tab updates the other. Cheap and
  // matches how the theme provider tracks the user's preference.
  useEffect(() => {
    function onStorage(event: StorageEvent): void {
      if (event.key !== STORAGE_KEY) return;
      setCollapsedState(event.newValue === 'true');
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return { collapsed, setCollapsed, toggleCollapsed };
}
